import { FunctionComponent, useEffect, useState } from "react";
import { generatePath, RouteComponentProps, withRouter } from "react-router-dom";
import { Permission } from "../../../api/authApi";
import { BaseInfo, Guid } from "../../../api/baseApi";
import { getBaseChannels, getBaseChannelsForPriorityList, TimeSlotModel } from "../../../api/channelsApi";
import { createScheduledPriorityList, CreateScheduledPriorityListRequest, enableScheduledListTimeSlot, getScheduledListTimeSlots, getScheduledPriorityLists, removeScheduledListTimeSlots, ScheduledPriorityList, updateScheduledPriorityList, UpdateScheduledPriorityListRequest } from "../../../api/priorityListApi";
import { createUser, CreateUserRequest, getUsersList, suspendUser, unSuspendUser, updateUser, UpdateUserRequest, User } from "../../../api/usersApi";
import { createPathWithId, Routes } from "../../../constants/routes";
import { Button } from "../../components/Button";
import FormCheckbox from "../../components/formRow/FormCheckbox";
import FormMultiSelect from "../../components/formRow/FormMultiSelect";
import { FormInput, FormRow, FormRowHorizontal, SelectOption } from "../../components/formRow/FormRow";
import GlobalSpinner from "../../components/globalsSpinner/GlobalSpinner";
import IconButton from "../../components/iconButton/IconButton";
import Schedule from "../../components/schedule/Schedule";
import { CenterModal, RightModal } from "../../components/sideModal/Modal";
import InnerLayout from "../innerLayout/InnerLayout";

interface UsersListProps extends RouteComponentProps<any>{
    logOut : () => void;
    toogleCollapse: () => void;
    isCollapsed: boolean;
}
 
const UsersList: FunctionComponent<UsersListProps> = (props : UsersListProps) => {
    let {history} = props;
    let [isLoading, setIsloading] = useState(false);
    let [showModal, setShowModal] = useState<boolean>(false);
    let [userName, setUserName] = useState<string>("");
    let [userNameError, setUserNameError] = useState<boolean>(false);
    let [email, setEmail] = useState<string>("");
    let [emailError, setEmailError] = useState<boolean>(false);
    let [password, setPassword] = useState<string>("");
    let [passwordError, setPasswordError] = useState<boolean>(false);
    let [permissions, setPermissions] = useState<Permission[]>([]);

    let [users, setUsers] = useState<User[]>([]);
    let [userIdForEdit, setUserIdForEdit] = useState<Guid>();

    useEffect(()=>{
        async function getData(){
            setIsloading(true);
            await Promise.all([updateUserList()]);
            setIsloading(false);
        }
        getData();
    },[])
    
    async function updateUserList(){
        setIsloading(true)
        let users = await getUsersList();
        if(users.type == "succes") setUsers(users.data.items);
        setIsloading(false);
    }


    const showCreateModal = () => {
        setShowModal(true);
    }

    const renderRow = (user: User) => {
        return <tr key={user.id}>
            <td>{user.id}</td>
            <td>{user.name}</td>
            <td>{user.email}</td>
            <td>{user.isSuspended ? "Yes" : "No"}</td>
            <td>
                <IconButton tooltipText="Notification settings" type="report2" onClick={()=>{goToSettingsNotification(user.id)}}/>
                <IconButton tooltipText={user.isSuspended ? "Play" : "Stop"} type={user.isSuspended ? "play" : "stop-play"} 
                onClick={() => {user.isSuspended ? unsuspend(user.id) : suspend(user.id)}}/>
                <IconButton tooltipText="Edit" type="edit" onClick={() => {showEditModal(user)}}/>
            </td>
        </tr>
    }

    const showEditModal = (user: User) => {
        setUserIdForEdit(user.id);
        setUserName(user.name);
        setEmail(user.email);
        setPassword('');
        setPermissions(user.permissions);
        setShowModal(true);
    }

    const closeModal = () => {
        setUserName('');
        setEmail('');
        setPassword('');
        setPermissions([]);
        setUserIdForEdit(undefined);
        setShowModal(false);
    }

    const saveUser = async () => {
        let isError = true;
        if(userName === ""){
            setUserNameError(true);
            isError = false;
        }
        if(email === ""){
            setEmailError(true);
            isError = false;
        }
        if(userIdForEdit === undefined && password === ""){
            setPasswordError(true);
            isError = false;
        }

        if(!isError) return;

        if(userIdForEdit === undefined){
            let request : CreateUserRequest = {
                name: userName, 
                email: email, 
                password: password, 
                permissions: permissions};
            await createUser(request);
        }
        else{
            let request : UpdateUserRequest =  {
                name: userName, 
                email: email, 
                permissions: permissions };
            await updateUser(userIdForEdit, request);
        }
        await updateUserList();
        closeModal();
    }

    const updatePermission = ( permission: Permission) =>{
        if(permissions.findIndex(it => it == permission) !== -1){
            setPermissions(permissions.filter(it => it !== permission));
        }
        else{
            setPermissions([...permissions, permission]);
        }
    } 

    const renderPermission = (name: string, permission: Permission) => {
        return <FormRowHorizontal>
            <div className="status-container">
                <span>{name}</span>
                <FormCheckbox
                    checked={permissions.findIndex(it => it == permission) !== -1}
                    id={`permission_${name}`}
                    onChange={()=> updatePermission(permission)}
                />
            </div>
        </FormRowHorizontal>
    }

    const suspend = async (id: Guid) => {
        await suspendUser(id);
        await updateUserList();
    }

    const unsuspend = async (id: Guid) => {
        await unSuspendUser(id);
        await updateUserList();
    }

    const goToSettingsNotification = (id: Guid) => {
        history.push(`${Routes.Users}/${id}/notifications`)
    }

    return (  
    <InnerLayout 
        isCollapsed={props.isCollapsed}
        logOut={props.logOut}
        toogleCollapse={props.toogleCollapse}
        activeTab="users">
            <>
                <div className="d-flex justify-content-end align-items-center mb-20 pt-50">
                    <Button color="green"  label="Create User" onClick={showCreateModal} />
                </div>
                <table className="table">
                    <thead>
                        <tr>
                            <td>Id</td>
                            <td>Name</td>
                            <td>Email </td>
                            <td>Suspended</td>
                            <td></td>
                        </tr>
                    </thead>
                <tbody>
                    {users.map(it => renderRow(it))}
                </tbody>
            </table>
            
            <RightModal title={userIdForEdit === undefined ? "Add new user" : "Edit new user"} 
                show={showModal}
                onRequestClose={closeModal}
                >
                    <FormRow>
                            <FormInput 
                                id="user-name"
                                title="Username"
                                isError={userNameError}
                                errorMessage="Please enter user name"
                                onChange={e => { setUserName(e.target.value); setUserNameError(false);}}
                                value={userName}
                            />
                    </FormRow>
                    <FormRow>
                            <FormInput 
                                id="user-email"
                                title="Email"
                                isError={emailError}
                                errorMessage="Please enter email"
                                onChange={e => { setEmail(e.target.value); setEmailError(false);}}
                                value={email}
                            />
                    </FormRow>  
                    { !!!userIdForEdit &&
                    <FormRow>
                        <FormInput 
                            id="user-password"
                            title="Password"
                            isError={passwordError}
                            errorMessage="Please enter password"
                            onChange={e => { setPassword(e.target.value); setPasswordError(false);}}
                            value={password}
                        />
                    </FormRow> } 
                    {renderPermission("Campaign", Permission.Campaigns)}
                    {renderPermission("Networks", Permission.Channels)}
                    {renderPermission("Dashboard", Permission.Dashboard)}
                    {renderPermission("Media", Permission.Media)}
                    {renderPermission("Players", Permission.Players)}
                    {renderPermission("Reports", Permission.Reports)}
                    {renderPermission("Users", Permission.Users)}
                    {renderPermission("PriorityList", Permission.PriorityList)}
                    {renderPermission("PlayNowList", Permission.PlayNowList)}
                    {renderPermission("Mosaic", Permission.MosaicScreen)}
                    {renderPermission("Settings", Permission.Settings)}

                    <div className="modal-btn-group">
                        <Button color="green" label="Save" onClick={saveUser}/>
                        <Button color="gray" label="Cancel" onClick={closeModal}/>
                    </div>
            </RightModal>
            <GlobalSpinner isShow={isLoading} />
            </>
           
    </InnerLayout> );
}
 
export default withRouter(UsersList);